import React, { useEffect, useState } from 'react';
import { redirect, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config';
import OptionIcon from '../../assets/option.png';
import Swal from 'sweetalert2';
import Select from 'react-select';
import {
  Box,
  Card,
  Typography,
  Button,
  Divider,
  LinearProgress,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import loadingGif from '../../assets/loading.gif';
import './OrganizationMembers.css';

const OrganizationMembers = () => {
  const [orgName, setOrgName] = useState('N/A');
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [createEnabled, setCreateEnabled] = useState(false);
  const [updateEnabled, setUpdateEnabled] = useState(false);
  const [deleteEnabled, setDeleteEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adminStatusMap, setAdminStatusMap] = useState({});
  const [deleteMemberDialogOpen, setDeleteMemberDialogOpen] = useState(false);
  const [selectedDeleteMemberObj, setSelectedDeleteMemberObj] = useState(null);
  const [deleteMemberConfirmName, setDeleteMemberConfirmName] = useState("");


  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrgMembers = async () => {
      const token = localStorage.getItem('token');
      const orgId = localStorage.getItem('selectedOrg');

      if (!token || !orgId) {
        navigate('/unauthorized');
        return;
      }

      setLoading(true);

      try {
        const orgEnabledResponse = await axios.get(`${API_BASE_URL}/org/org-enabled`, {
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: orgId,
          },
        });

        const { isEnabled } = orgEnabledResponse.data;

        if (!isEnabled) {
          Swal.fire({
            title: 'Organization Disabled',
            text: 'This organization has been disabled by the admin. Please contact support for further assistance.',
            icon: 'error',
            confirmButtonText: 'OK',
          }).then(() => {
            navigate('/unauthorized');
          });

          return;
        }

        const create_enabled = await axios.get(`${API_BASE_URL}/org-members/org-members-create`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { orgId },
        });

        if (create_enabled.status === 403) {
          navigate('/unauthorized');
          return;
        }

        if (create_enabled.data) {
          if (!create_enabled.data.permissions.readPermission) {
            navigate('/unauthorized');
            return;
          }
          setCreateEnabled(create_enabled.data.permissions.createPermission || false);
          setUpdateEnabled(create_enabled.data.permissions.updatePermission || false);
          setDeleteEnabled(create_enabled.data.permissions.deletePermission || false);
        }

        const orgResponse = await axios.get(`${API_BASE_URL}/org/organization-settings/organization-name`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { orgId },
        });

        if (orgResponse.status === 403) {
          navigate('/unauthorized');
          return;
        }
        setOrgName(orgResponse.data.orgName || 'N/A');

        const allMembers = await axios.get(`${API_BASE_URL}/org-members/all-members`, {
          headers: {
            Authorization: `Bearer ${token}`,
            selectedOrg: orgId,
          },
        });

        const { members, roles } = allMembers.data;
        setMembers(members || []);
        setRoles(roles || []);

        // Fetch and set admin statuses for all members
        const statusMap = {};
        await Promise.all(members.map(async (member) => {
          const isAdminUser = await isAdmin(member.memberEmail);
          statusMap[member.memberEmail] = isAdminUser;
        }));
        setAdminStatusMap(statusMap);

      } catch (error) {
        console.log('Failed to fetch Org Members:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrgMembers();
  }, [navigate]);

  const handleCreateMember = () => {
    navigate('/organization/members/create');
  };

  const isAdmin = async (email) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/is-admin`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          SelectedOrg: localStorage.getItem('selectedOrg'),
          email: email,
        },
      });

      return response.data.isAdmin === 1;
    } catch (error) {
      console.error('Failed to fetch admin status:', error);
      return false;
    }
  };

  const handleMemberChange = async (memberEmail, newRoleId, newEnabledStatus) => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('selectedOrg');

    Swal.fire({
      title: 'Updating...',
      text: 'Please wait while we update the member.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await axios.post(`${API_BASE_URL}/org-members/update-member`, {
        memberEmail: memberEmail,
        roleId: newRoleId,
        isEnabled: newEnabledStatus,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          SelectedOrg: orgId,
        },
      });

      Swal.close();

      if (response.status === 200) {
        Swal.fire({
          title: 'Success!',
          text: 'Member updated successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          window.location.reload();
        });
      }
    } catch (error) {
      console.error('Failed to update member:', error);
      Swal.close();
      Swal.fire({
        title: 'Error!',
        text: 'Failed to update member. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
      }).then(() => {
        window.location.reload();
      });
    }
  };

  const handleDeleteMember = async (memberId, memberEmail) => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('selectedOrg');

    try {
      const response = await axios.post(`${API_BASE_URL}/org-members/delete`, {
        memberId: memberId,
        memberEmail: memberEmail,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          SelectedOrg: orgId,
        },
      });

      Swal.close();
      
      if (response.status === 201) {
        Swal.fire({
          title: 'Success!',
          text: 'Member deleted successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          window.location.reload();
        });
      }
    } catch (error) {
      console.error('Failed to delete member:', error);
      Swal.close();
      Swal.fire({
        title: 'Error!',
        text: 'Failed to delete member. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
      }).then(() => {
        window.location.reload();
      });
    }
  };

  if (loading) {
    return (
      <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
          backgroundColor: '#fff',
      }}>
          <img src={loadingGif} alt="Loading..." style={{ width: '30%', height: '30%', objectFit: 'contain' }} />
      </div>
      );
    }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#f5f5f5', width: '100%' }}>
          <Card sx={{ width: '100%', p: 4, boxShadow: 3 }}>
            <div className="organizations-header">
              <h1>Organization Members: {orgName}</h1>
              {createEnabled && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreateMember}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Create Member
                  </Button>
                </div>
              )}
            </div>
            <TableContainer component={Paper} sx={{ mb: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.filter(member => !adminStatusMap[member.memberEmail]).map((member) => (
                    <TableRow key={member.memberId}>
                      <TableCell>{member.memberEmail}</TableCell>
                      <TableCell>
                        <Select
                        options={roles.map(role => ({ value: role.role_id, label: role.role_name }))}
                        value={member.memberRole ? { 
                          value: member.memberRole.role_id, 
                          label: member.memberRole.role_name 
                        } : null}
                        onChange={(selectedOption) => {
                          if (selectedOption) {
                            handleMemberChange(member.memberEmail, selectedOption.value, member.is_enabled);
                          }
                        }}
                        isDisabled={adminStatusMap[member.memberEmail] || false}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            margin: '10px 0',
                            maxWidth: '50vw',
                          }),
                          menu: (provided) => ({
                            ...provided,
                            zIndex: 999,
                          }),
                        }}
                        menuPortalTarget={document.body} 
                      />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={member.is_enabled}
                          onChange={e =>handleMemberChange(member.memberEmail, member.memberRole.role_id, !member.is_enabled)}
                          disabled={!updateEnabled}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          sx={{ mr: 1 }}
                          onClick={() => navigate(`/organization/members/edit/${member.memberId}`)}
                          disabled={!updateEnabled}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => {
                            setSelectedDeleteMemberObj(member);
                            setDeleteMemberDialogOpen(true);
                            setDeleteMemberConfirmName("");
                          }}
                          disabled={!deleteEnabled}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
      </Box>
      <Dialog
        open={deleteMemberDialogOpen}
        onClose={() => setDeleteMemberDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ color: "black" }}>Delete Member</DialogTitle>
        <DialogContent>
          <div style={{ marginBottom: 16 }}>
            Please type <b>{selectedDeleteMemberObj?.memberEmail}</b> to confirm deletion.
          </div>
          <TextField
            label={`${selectedDeleteMemberObj?.memberEmail}`}
            variant="outlined"
            multiline
            rows={1}
            fullWidth
            value={deleteMemberConfirmName}
            onChange={e => setDeleteMemberConfirmName(e.target.value)}
            autoFocus
            margin="normal"
            helperText={`Type the member name exactly as shown above to confirm deletion.`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteMemberDialogOpen(false)} style={{ padding: "6px 12px" }}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (selectedDeleteMemberObj && deleteMemberConfirmName === selectedDeleteMemberObj.memberEmail) {
                handleDeleteMember(selectedDeleteMemberObj.memberId, selectedDeleteMemberObj.memberEmail);
                setDeleteMemberDialogOpen(false);
              }
            }}
            variant="contained"
            color="error"
            style={{ padding: "6px 12px" }}
            disabled={
              !selectedDeleteMemberObj ||
              !deleteMemberConfirmName ||
              (selectedDeleteMemberObj?.memberEmail !== deleteMemberConfirmName)
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrganizationMembers;
